<script lang="ts">
  import KeyboardHints from '../KeyboardHints.svelte';
  import { hasCommandModifier, isEditableTarget } from '../keyboard';
  import { groupDigits } from '../../lib/complexity/format';
  import type { LabStep } from '../../lib/router';
  import {
    emptyQueue,
    parseValue,
    preparedQueuePuzzles,
    queueModel,
    QUEUE_PUZZLES,
    swiftArray,
    totalDequeueCost,
    waiting,
    type QueueMode,
  } from '../../lib/labs';
  import QueueBoard from './QueueBoard.svelte';
  import LabLog from './LabLog.svelte';
  import PredictPanel from './PredictPanel.svelte';
  import ReplayPanel from './ReplayPanel.svelte';
  import WindowIntro from './WindowIntro.svelte';
  import { finishIntro, intro, prefersReducedMotion, sessions } from './lab-session.svelte';

  /** ラボ 2（labs.md、components.md §3.2 QueueLab）。 */
  interface Props {
    step: LabStep;
    ongo: (step: LabStep) => void;
  }

  let { step, ongo }: Props = $props();

  const session = sessions.queue;
  const puzzles = preparedQueuePuzzles();
  const reduced = prefersReducedMotion();

  let valueText = $state('1');
  let valueInput: HTMLInputElement | undefined = $state();
  let status = $state('');
  let compare = $state(false);

  const q = $derived(session.state);
  const mode = $derived(q.mode);
  const lastCode = $derived(session.last?.entry?.code ?? '');
  const lastTag = $derived(reduced && session.last?.entry ? session.last.entry.tag : null);
  const passedCount = $derived(session.run.steps.filter((s) => s.entry.tag === '通過').length);
  /** 通すのにかかった手間の合計（head 方式は添字の更新 1 回、詰める方式は動いた人数）。 */
  const totalCost = $derived(
    session.run.steps
      .filter((s) => s.entry.tag === '通過')
      .reduce((a, s) => a + (mode === 'head' ? 1 : s.state.items.length), 0),
  );

  function say(text: string) {
    status = text;
  }

  function enqueue() {
    const v = parseValue(valueText);
    if (v === null) {
      say('値に -999〜999 の整数を入れてください。');
      valueInput?.focus();
      return;
    }
    say(session.apply({ type: 'enqueue', value: v }).message);
    if (v < 999) valueText = String(v + 1);
  }

  function dequeue() {
    say(session.apply({ type: 'dequeue' }).message);
  }

  function count() {
    say(session.apply({ type: 'count' }).message);
  }

  function undo() {
    if (session.undo()) say('1 手戻しました。');
  }

  function reset() {
    session.reset();
    say('最初からにしました。');
  }

  function setMode(m: QueueMode) {
    if (m === mode) return;
    session.reset(emptyQueue(m));
    session.editorCode = null;
    say(
      m === 'shift'
        ? '詰める方式（removeFirst()）にしました。記録は最初からです。'
        : 'head 方式にしました。記録は最初からです。',
    );
  }

  function send() {
    session.sendToEditor();
    ongo(3);
  }

  function loadExample() {
    session.reset(emptyQueue('head'));
    for (const op of QUEUE_PUZZLES[0]?.ops ?? []) session.apply(op);
  }

  function onkeydown(e: KeyboardEvent) {
    if (step !== 1 || !intro.queue || e.defaultPrevented) return;
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'z') {
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      undo();
      return;
    }
    if (hasCommandModifier(e)) return;
    if (isEditableTarget(e.target)) {
      if (e.target === valueInput && e.key === 'Enter') {
        e.preventDefault();
        enqueue();
      } else if (e.target === valueInput && e.key === 'Escape') {
        valueInput?.blur();
      }
      return;
    }
    const actions: Record<string, () => void> = {
      e: enqueue,
      d: dequeue,
      c: count,
      v: () => {
        valueInput?.focus();
        valueInput?.select();
      },
    };
    const act = actions[e.key.toLowerCase()];
    if (act) {
      e.preventDefault();
      act();
    }
  }
</script>

<svelte:window {onkeydown} />

{#if !intro.queue}
  <WindowIntro ondone={() => finishIntro('queue')} />
{:else if step === 1}
  <div class="lab" data-testid="lab-queue">
    <section class="panel board" aria-labelledby="board-title" data-testid="lab-board-slot">
      <div class="board-head">
        <h2 id="board-title">盤面</h2>
        <label class="switch">
          <input
            type="checkbox"
            role="switch"
            checked={mode === 'shift'}
            onchange={(e) => setMode(e.currentTarget.checked ? 'shift' : 'head')}
            data-testid="queue-shift"
          />
          詰める（<code>removeFirst()</code>）
        </label>
      </div>
      <QueueBoard
        items={q.items}
        head={q.head}
        shift={mode === 'shift'}
        offset={mode === 'shift' ? passedCount : 0}
        entered={lastCode.startsWith('queue.append')}
        glowWaiting={lastCode.startsWith('print(queue.count')}
        changeTag={lastTag}
      />
      <div class="controls">
        <div class="value field">
          <label for="queue-val">値 <kbd>V</kbd></label>
          <input
            id="queue-val"
            type="text"
            inputmode="numeric"
            bind:this={valueInput}
            bind:value={valueText}
          />
        </div>
        <button type="button" class="btn btn-primary" onclick={enqueue} data-testid="queue-enqueue"
          >並ばせる <kbd>E</kbd></button
        >
        <button type="button" class="btn" onclick={dequeue} data-testid="queue-dequeue"
          >通す <kbd>D</kbd></button
        >
        <button type="button" class="btn" onclick={count}>残りの人数 <kbd>C</kbd></button>
      </div>
      <div class="controls">
        <button type="button" class="btn btn-quiet" onclick={undo}>1 手戻す <kbd>⌘Z</kbd></button>
        <button type="button" class="btn btn-quiet" onclick={reset}>最初から</button>
        <button type="button" class="btn btn-quiet" onclick={() => (compare = !compare)}
          >100 人で比べる</button
        >
      </div>
      <p class="status-line" role="status" aria-live="polite">{status}</p>
      {#if compare}
        <p class="compare" role="status">
          100 人並ばせて全員通すと、head 方式は
          <strong class="num">{groupDigits(totalDequeueCost('head', 100))}</strong>
          ステップ、詰める方式は
          <strong class="num">{groupDigits(totalDequeueCost('shift', 100))}</strong>
          ステップ（99 + 98 + … + 0）です。<a href="#/meter">計算量メーター</a>で N
          を増やして確かめられます。
        </p>
      {/if}
      <dl class="counters" aria-live="polite">
        <div>
          <dt>まだ通っていない人</dt>
          <dd class="num">{waiting(q).length}</dd>
        </div>
        <div>
          <dt>操作</dt>
          <dd class="num">{session.run.steps.length}</dd>
        </div>
        <div>
          <dt>今の操作で動いた箱</dt>
          <dd class="num">{session.last?.moved ?? 0}</dd>
        </div>
        <div>
          <dt>通すのにかかったステップ（合計）</dt>
          <dd class="num">{groupDigits(totalCost)}</dd>
        </div>
      </dl>
    </section>

    <section class="panel log" aria-labelledby="log-title" data-testid="lab-log-slot">
      <h2 id="log-title">記録（Swift）</h2>
      <LabLog
        declarations={queueModel.declarations(session.run.initial)}
        entries={session.entries}
        code={session.code}
        footer={mode === 'head'
          ? `print(queue[head...])  // ${swiftArray(waiting(q))}`
          : `print(queue)  // ${swiftArray(q.items)}`}
        onsend={send}
      />
      <p class="text-soft small">
        {#if mode === 'head'}
          通った人は配列から消さず、<code>head</code> だけを進めます。<code>head</code>
          より前は二度と見ません。
        {:else}
          <code>removeFirst()</code> は、通すたびに残りの全員を 1 つ前へ動かします。
        {/if}
      </p>
    </section>
  </div>
  <KeyboardHints
    hints={[
      { keys: ['E'], label: '並ばせる' },
      { keys: ['D'], label: '通す' },
      { keys: ['C'], label: '残りの人数' },
      { keys: ['V'], label: '値の欄へ' },
      { keys: ['⌘Z'], label: '1 手戻す' },
      { keys: ['1', '2', '3'], label: '段階の切り替え' },
    ]}
  />
{:else if step === 2}
  <PredictPanel model={queueModel} {puzzles} labels={['queue']} />
{:else}
  <ReplayPanel labId="queue" {session} labels={['queue']} onexample={loadExample} />
{/if}

<style>
  .board-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .board-head h2 {
    margin: 0;
  }
  .switch {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--fs-sm);
    min-height: var(--tap-min);
  }
  .compare {
    margin: 0;
    padding: var(--space-3);
    border-radius: var(--radius-md);
    background: var(--accent-soft);
    font-size: var(--fs-sm);
  }
</style>
